import { ReactNode, useEffect } from "react";

type DrawerProps = {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number | string;
  /** When true, renders a <form> wrapper so submit buttons inside the drawer work natively. */
  asForm?: boolean;
  onSubmit?: (e: React.FormEvent) => void;
};

export default function Drawer({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 560,
  asForm = false,
  onSubmit
}: DrawerProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const Panel = asForm ? "form" : "div";
  const panelProps = asForm ? { onSubmit } : {};

  return (
    <div className="drawer-backdrop" role="presentation" onClick={onClose}>
      <Panel
        className="drawer"
        role="dialog"
        aria-modal="true"
        style={{ width: typeof width === "number" ? `min(${width}px, 96vw)` : width }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        {...panelProps}
      >
        <header className="drawer-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <div className="soft" style={{ fontSize: 12, marginTop: 4 }}>{subtitle}</div> : null}
          </div>
          <button type="button" className="linkbtn drawer-close" onClick={onClose} aria-label="Close drawer">×</button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-foot">{footer}</footer> : null}
      </Panel>
    </div>
  );
}
